import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../../shared/contexts/AuthContext';
import config from '../../../config/config';
import './LoginScreen.css';

// Fallback значения на случай, если config не загрузился
const FALLBACK_CONFIG = {
  app: {
    defaultRoute: '/dashboard'
  }
};

// Используем config или fallback
const currentConfig = config || FALLBACK_CONFIG;

const LoginScreen: React.FC = () => {
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Получаем путь, с которого пользователь был перенаправлен
  const from = location.state?.from?.pathname || currentConfig.app.defaultRoute;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!phone || !password) return;

    setIsLoading(true);
    setError(null);

    try {
      await login({ phone, password });
      // Перенаправляем пользователя на страницу, с которой он пришел, или на дашборд
      // Для страниц приглашения используем window.location.href, чтобы избежать конфликта с ProtectedRoute
      if (from.startsWith('/invite/')) {
        window.location.href = from;
      } else {
        navigate(from, { replace: true });
      }
    } catch (error: any) {
      console.error('Ошибка входа:', error);
      
      // Обрабатываем различные типы ошибок
      if (error.response?.status === 401) {
        setError('Неверный номер телефона или пароль');
      } else if (error.response?.status === 400) {
        // Парсим ошибки валидации с сервера
        if (error.response.data?.validationErrors) {
          const validationErrors = error.response.data.validationErrors;
          const errorMessages = validationErrors.map((err: any) => 
            `${err.field}: ${err.errors.join(', ')}`
          ).join('; ');
          setError(`Ошибка валидации: ${errorMessages}`);
        } else if (error.response.data?.error) {
          setError(error.response.data.error);
        } else {
          setError('Пожалуйста, проверьте правильность введенных данных');
        }
      } else if (error.response?.status >= 500) {
        setError('Ошибка сервера. Попробуйте позже');
      } else if (error.code === 'NETWORK_ERROR') {
        setError('Ошибка соединения с сервером. Проверьте интернет-соединение');
      } else if (error.message) {
        setError(error.message);
      } else {
        setError('Произошла ошибка при входе. Попробуйте еще раз');
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="login-screen">
      <div className="login-container">
        <div className="login-header">
          <h1 className="login-title">Вход в систему</h1>
          <p className="login-subtitle">Ryaskov.pro - Система управления ремонтом</p>
        </div>

        <form onSubmit={handleSubmit} className="login-form">
          {error && (
            <div className="error-message">
              <i className="ri-error-warning-line"></i>
              {error}
            </div>
          )}

          <div className="form-group">
            <label htmlFor="phone" className="form-label">
              Телефон или Email
            </label>
            <input
              id="phone"
              type="text"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="Телефон или email"
              className="form-input"
              required
              disabled={isLoading}
              autoComplete="username"
            />
            <small className="form-help">
              Введите номер телефона или email
            </small>
          </div>

          <div className="form-group">
            <label htmlFor="password" className="form-label">
              Пароль
            </label>
            <input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Введите ваш пароль"
              className="form-input"
              required
              disabled={isLoading}
              autoComplete="current-password"
            />
          </div>

          <button
            type="submit"
            className="login-button"
            disabled={isLoading || !phone || !password}
          >
            {isLoading ? (
              <>
                <i className="ri-loader-4-line ri-spin"></i>
                Вход...
              </>
            ) : (
              <>
                <i className="ri-login-box-line"></i>
                Войти
              </>
            )}
          </button>
        </form>

        <div className="login-footer">
          <p className="help-text">
            <button
              type="button"
              className="login-register-link"
              onClick={() => navigate('/forgot-password')}
              disabled={isLoading}
            >
              Забыли пароль?
            </button>
          </p>
          <p className="help-text">
            Нет аккаунта?{' '}
            <button
              type="button"
              className="login-register-link"
              onClick={() => navigate('/register')}
              disabled={isLoading}
            >
              Зарегистрироваться
            </button>
          </p>
          <button
            onClick={() => navigate('/')}
            className="login-back-button"
            disabled={isLoading}
          >
            <i className="ri-arrow-left-line"></i>
            Вернуться на главную
          </button>
        </div>
      </div>
    </div>
  );
};

export default LoginScreen;
